import MomentUtils from "@date-io/moment";
import { MenuItem, Select } from "@material-ui/core";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { useFormik } from "formik";
import moment from "moment";
import { X } from "phosphor-react";
import React, { useMemo } from "react";
import * as Yup from "yup";
import { fpatch, fpost } from "../../API/callsAPI";
import commonStyles from "../../Utils/commonStyles";
import CustomInputFieldWithFormik from "../Utilities/CustomInputFields/CustomInputFieldWithFormik";

const CustomPageEntryOrEditRecord = ({
  fields,
  pageId,
  handleClose,
  handleRefresh,
  type,
  editData,
  rows,
  setSnackValue,
  pageData,
}) => {
  const commonClasses = commonStyles();

  const fieldsToEdit = useMemo(() => {
    return type === "new"
      ? fields.filter((field) => field.type !== "media")
      : fields.filter(
          (field) =>
            field.type !== "media" &&
            pageData?.fields?.some((pageField) => pageField.name === field.name)
        );
  }, [fields, pageData]);

  const getInitialValues = () => {
    return fieldsToEdit.reduce((acc, field) => {
      acc[field.identifier] =
        type === "edit" && editData?.[field.name] !== undefined
          ? editData[field.name]
          : field.default_value || "";
      return acc;
    }, {});
  };

  // Build dynamic validation schema for required fields
  const validationSchema = Yup.object(
    fieldsToEdit.reduce((acc, field) => {
      acc[field.identifier] = Yup.string().required(
        `${field.name} is required`
      );
      return acc;
    }, {})
  );

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    onSubmit: async (values) => {
      try {
        const formattedValues = Object.keys(values).reduce((acc, key) => {
          const field = fieldsToEdit.find((item) => item.identifier === key);

          if (field?.type === "date_time") {
            const value = values[key];
            if (!value) {
              acc[key] = "";
            } else {
              acc[key] = moment(value).isValid()
                ? moment(value).format("YYYY-MM-DD HH:mm:ss")
                : value;
            }
          } else {
            acc[key] = values[key];
          }

          return acc;
        }, {});

        if (type === "new") {
          const addFieldData = {
            url: `trackables/custom-page/${pageId}/data/`,
            data: formattedValues,
          };
          const res = await fpost(addFieldData);
          if (res.status === 200 || res.status === 201) {
            setSnackValue({
              isOpen: true,
              message: `SuccessFully Added Row`,
              isError: false,
            });
            handleRefresh();
            handleClose();
          }
        } else {
          const index = rows.findIndex((obj) => obj.id === editData.id);
          const updateFieldData = {
            url: `trackables/custom-page/${pageId}/update-row/${editData.id}/`,
            data: formattedValues,
          };
          const res = await fpatch(updateFieldData);
          if (res.status === 200 || res.status === 201) {
            setSnackValue({
              isOpen: true,
              message: `SuccessFully Edited Row`,
              isError: false,
            });
            handleRefresh();
            handleClose();
          }
        }
      } catch (error) {
        setSnackValue({
          isOpen: true,
          message: "Error On Add New Row",
          isError: true,
        });
        console.log(error);
      }
    },
  });

  const getOptionsForDropdown = (field) => {
    const pageField = pageData?.fields?.find(
      (pageField) => pageField.id === field.id
    );
    return Object.values(pageField?.options.options) || [];
  };

  return (
    <Card
      sx={{
        width: { md: "450px", lg: "450px", xl: "500px" },
        backgroundColor: "#1B1D28",
        color: "white",
        borderRadius: "20px",
        padding: "10px",
      }}
    >
      <CardContent
        sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
      >
        <Box className={commonClasses.flexBetween}>
          <Typography sx={{ fontSize: "20px !important", fontWeight: 500 }}>
            {type === "new" ? "Add New Record" : "Update Record"}
          </Typography>
          <Button
            onClick={handleClose}
            sx={{ color: "white", minWidth: 0, padding: 0 }}
          >
            <X size={22} />
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "max-content",
            maxHeight: "500px",
            overflowY: "auto",
            paddingRight: "5px",
          }}
        >
          {fieldsToEdit.map((sin) =>
            sin.type === "dropdown" ? (
              <Box key={sin.identifier}>
                <Typography>{sin?.name}</Typography>
                <div className={commonClasses.dropdownContainer}>
                  <Select
                    style={{ width: "100%" }}
                    disableUnderline
                    className={commonClasses.select}
                    value={formik.values[sin?.identifier]}
                    onChange={(e) =>
                      formik.setFieldValue(sin?.identifier, e.target.value)
                    }
                  >
                    {getOptionsForDropdown(sin)?.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
                {formik.touched[sin.identifier] &&
                  formik.errors[sin.identifier] && (
                    <Typography color="error">
                      {formik.errors[sin.identifier]}
                    </Typography>
                  )}
              </Box>
            ) : sin.type === "date_time" ? (
              <Box key={sin.identifier}>
                <Typography>{sin?.name}</Typography>
                <MuiPickersUtilsProvider
                  libInstance={moment}
                  utils={MomentUtils}
                >
                  <DateTimePicker
                    value={formik.values[sin?.identifier]}
                    onChange={(value) =>
                      formik.setFieldValue(sin?.identifier, value)
                    }
                    format="YYYY-MM-DD HH:mm:ss"
                    renderInput={(params) => <TextField {...params} />}
                  />
                </MuiPickersUtilsProvider>
              </Box>
            ) : (
              <CustomInputFieldWithFormik
                key={sin.identifier}
                name={sin.identifier}
                formik={formik}
                isRequired={false}
                label={sin.name}
                isDisabled={false}
              />
            )
          )}
        </Box>
        <CardActions className={commonClasses.flexCenter}>
          <Button
            onClick={formik.handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: "#6C65D9",
              borderRadius: "10px",
              textTransform: "none",
            }}
            disabled={!(formik.dirty && formik.isValid)}
          >
            {type === "new" ? "Create Record" : "Update Record"}
          </Button>
        </CardActions>
      </CardContent>
    </Card>
  );
};

export default CustomPageEntryOrEditRecord;
